// src/pages/WardRulesPage.tsx
//
// ★★★ WAVE 13 (P8) — Annex A 19-21: THE WARD SUBSIDY RATE REGISTER, AND THE
// FIRST SCREEN IN THIS PRODUCT THAT CAN AUTHOR MONEY POLICY.
//
// ══ THE GAP THIS CLOSES ══
//
// `docs/requirements/RTM.md`: *"an authoring API … (a migration only, today)"*.
// `app.ward_rule_version` has priced CPW/FAW/CWM bills since Phase 7, and the
// only way a row ever entered it was a `psql` session or a demonstration seed
// script. PSD had no surface at all.
//
// ══ ⚠ WHAT THIS SCREEN MUST NEVER LET A READER BELIEVE ══
//
// **That any rate here came from the Authority.** The tender furnishes none for
// CPW, FAW or CWM (C-2, Q-18, Q-34). Three things carry that, and none of them is
// a sentence this file invented:
//
//   1. **The empty-register notice is the SERVER's** (`emptyRegisterNotice` on
//      the payload). It is a claim about the database, so a `.tsx` copy would go
//      on asserting it after the Authority supplies a rate. Its ABSENCE from the
//      payload is how this screen knows to stop saying it.
//   2. **The provenance note is the AUTHORING OFFICER's**, rendered verbatim in
//      its own column and never summarised. `rate-provenance.ts` prints the same
//      words beside every figure the rate produces, and this screen must not put
//      a second, differently-worded version of them anywhere.
//   3. **The "Authored" column** distinguishes a row a person typed from one a
//      migration or the demonstration seed wrote — which is exactly the
//      distinction that decides whether a provenance note was mandatory for it.
//
// ══ ⚠ THE STANDING BOUNDARY, RENDERED IN EVERY STATE ══
//
// Not conditional on the register being empty, for the reason `AuditArchivePage`'s
// own boundary gives: *"the moment such a notice is conditional, the state where
// it matters most is the state that hides it."* The state that matters most here
// is the one where a rate HAS been authored — because that is when a reader is
// most likely to assume somebody official supplied it.
import { plainProse } from '../lib/plain-words'
import { CodeBadge } from '../components/StatusBadge'
import { useState, type FormEvent } from 'react'
import { SgdsAlert, SgdsButton, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { FormResult } from '../components/FormResult'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { BoundedField } from '../components/BoundedField'
import { DatePicker } from '../components/DatePicker'
import { boundsProblems } from '../lib/published-bounds'
import { wholeNumberProblem } from '../components/FieldError'
import { targetValue } from '../lib/sgds-dom'
import { formatIsoDate, formatInstant } from '../lib/format'
import type { WardRule } from '../lib/types'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'

type WardRuleSortKey = 'id' | 'scheme' | 'wardClass' | 'subsidyBasisPoints' | 'effectiveFrom' | 'recordedAt'

const COLUMN_LABELS: Readonly<Record<WardRuleSortKey, string>> = {
  id: 'Rule',
  scheme: 'Scheme',
  wardClass: 'Ward',
  subsidyBasisPoints: 'Subsidy rate',
  effectiveFrom: 'Effective from',
  recordedAt: 'Recorded',
}

/**
 * ⚠ Only fields the SERVER publishes a bound for. `effectiveFrom` is a date
 * (a type check, not a field bound — see `FieldError`'s own header) and the
 * scheme is a `<select>` whose control IS the constraint.
 */
const BOUNDED_FIELDS = {
  wardClass: 'Ward class',
  subsidyBasisPoints: 'Subsidy rate (basis points)',
  /**
   * ⚠ NOT "(optional)". It is the one note in this product that is required, and
   * a label that said otherwise would be the first thing an officer read.
   */
  note: 'Where this rate came from',
} as const

/**
 * The bounded fields that exist only on an ESTABLISH.
 *
 * ⚠ A retirement sends neither the rate nor the note, so neither can be in
 * breach. Without naming them, a half-typed rate left in state before the box was
 * ticked keeps `problems.any` true and disables Save with no error visible
 * anywhere — the Wave 5 failure `FieldError.tsx`'s header records, and the reason
 * `WardClassesPage` carries the identical list.
 */
const ESTABLISH_ONLY_FIELDS = ['subsidyBasisPoints', 'note'] as const

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string }
  | { status: 'success'; message: string }

/** Basis points as an exact percentage. Integer arithmetic — never `bp / 100` on a money surface. */
function ratePercent(basisPoints: number): string {
  const whole = Math.trunc(basisPoints / 100)
  const fraction = Math.abs(basisPoints % 100)
  return `${whole}.${String(fraction).padStart(2, '0')}%`
}

/**
 * ⚠ **THE STANDING BOUNDARY.** Rendered in every state of this screen — see this
 * file's header for why it is unconditional.
 */
function AuthoringBoundary() {
  return (
    <div className="pt-unbuilt">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)]">
        No rate here is a confirmed rate
      </p>
      <p className="mt-2 text-[var(--sgds-body-color-subtle)]">
        PSD has given <strong>no confirmed subsidy rate</strong> for CPW, FAW or CWM, so
        nothing is seeded in any environment this product ships. Every rate in this register was typed by somebody:
        here, or by the demonstration seed script. A rate written on this screen{' '}
        <strong>prices every future CPW/FAW/CWM bill</strong> for its ward and service-date window, and the sentence
        you record beside it is printed verbatim on the bill detail screen, the cost estimate, the settlement batch
        table and their exports.
      </p>
      <p className="mt-2 font-semibold">
        Treat this as the place a confirmed rate is entered once it exists, not as evidence that one
        does.
      </p>
    </div>
  )
}

export function WardRulesPage() {
  const { sort, onSort, interacted } = useSort<WardRuleSortKey>()
  const [scheme, setScheme] = useState('')
  const wardRules = useApi(
    () => api.wardRules({ ...sortParams(sort), ...(scheme.length > 0 && { scheme }) }),
    [sort.column, sort.dir, scheme],
  )

  /**
   * One rule key's whole version history.
   *
   * ⚠ Resolves to `null` rather than calling the route with an empty id: a
   * `GET /api/reference/ward-rules/` is the LIST route, which would return the
   * whole register into a component rendering it as one key's history — the
   * "answered something adjacent" failure `WardClassesPage` names for the same
   * control.
   */
  const [historyId, setHistoryId] = useState('')
  const history = useApi(
    () => (historyId.length === 0 ? Promise.resolve(null) : api.wardRuleSeries(historyId)),
    [historyId],
  )

  const [formScheme, setFormScheme] = useState('CPW')
  const [wardClass, setWardClass] = useState('')
  const [basisPoints, setBasisPoints] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [note, setNote] = useState('')
  const [retireInstead, setRetireInstead] = useState(false)
  const [form, setForm] = useState<FormState>({ status: 'idle' })

  /**
   * ⚠ The SHAPE check only. `1.5` and `1e999` both reach the route and are both
   * refused there, but `1e999` becomes `Infinity` and `JSON.stringify` turns that
   * into `null` — so the officer is told the rate is MISSING from a field they
   * plainly filled in. The BOUND (`>= 0`) stays the route's and is reported from
   * the published `validation`, never restated here.
   */
  const basisPointsTypeProblem = wholeNumberProblem(basisPoints, BOUNDED_FIELDS.subsidyBasisPoints)

  const rows = wardRules.data?.wardRules ?? []
  const total = wardRules.data?.total ?? 0
  const validation = wardRules.data?.validation
  const emptyRegisterNotice = wardRules.data?.emptyRegisterNotice

  const problems = boundsProblems(
    validation,
    {
      wardClass: wardClass.trim().toUpperCase(),
      subsidyBasisPoints: basisPoints.trim(),
      note: note.trim(),
    },
    BOUNDED_FIELDS,
    { notSubmitted: retireInstead ? ESTABLISH_ONLY_FIELDS : [] },
  )
  const basisPointsProblem = basisPointsTypeProblem ?? problems.of.subsidyBasisPoints

  const canSubmit =
    wardClass.trim().length > 0 &&
    effectiveFrom.length > 0 &&
    !problems.any &&
    (retireInstead || (basisPoints.trim().length > 0 && basisPointsProblem === null && note.trim().length > 0))

  const ruleId = `${formScheme}:${wardClass.trim().toUpperCase()}`

  /**
   * ★★ WAVE 26 — Save FREEZES a sentence and asks; the write happens only from the
   * confirmation (`ConfirmWrite`'s header says why every irreversible write does
   * this now). The sentence is composed here, once, from the fields as they were
   * when Save was pressed — never re-derived from a form the officer may since
   * have edited. `danger`, because this is the one catalogue that prices bills.
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    setConfirming(
      retireInstead
        ? `Retire the ${formScheme} subsidy rate for ward ${wardClass.trim().toUpperCase()} (${ruleId}) from ${effectiveFrom}. Every CPW/FAW/CWM bill for this ward with a date of medical service on or after ${effectiveFrom} is refused: it does not fall back to the previous rate and is not priced at zero. There is no undo. A mistake is corrected by another version.`
        : `Author ${ruleId} at ${ratePercent(Number(basisPoints))} (${basisPoints.trim()} basis points) from ${effectiveFrom}. This prices every CPW/FAW/CWM bill for this ward with a date of medical service on or after ${effectiveFrom} at that rate, and the note you typed is printed verbatim beside every figure it produces. There is no undo. A mistake is corrected by another version.`,
    )
  }

  const performWrite = async () => {
    setConfirming(null)
    setForm({ status: 'submitting' })
    try {
      const outcome = (await api.supersedeWardRule(ruleId, {
        effectiveFrom,
        establish: !retireInstead,
        ...(!retireInstead && {
          subsidyBasisPoints: Number(basisPoints),
          note: note.trim(),
        }),
      })) as { outcome: string; consequence?: string }
      setForm({
        status: 'success',
        message:
          outcome.outcome === 'RETIRED'
            ? // ⚠ The SERVER's own sentence about what retiring causes, not this
              // screen's paraphrase. Retiring the last rate for a ward is the
              // operation whose consequence is least obvious from its name, and
              // the route states it in the response for exactly that reason.
              (outcome.consequence ?? `${ruleId} retired from ${effectiveFrom}.`)
            : `${ruleId} superseded from ${effectiveFrom}. Every CPW/FAW/CWM bill for this ward with a date of medical service on or after ${effectiveFrom} will be priced at ${basisPoints.trim().length > 0 ? ratePercent(Number(basisPoints)) : 'the new rate'}.`,
      })
      setWardClass('')
      setBasisPoints('')
      setEffectiveFrom('')
      setNote('')
      wardRules.reload()
      if (historyId.length > 0) history.reload()
    } catch (error) {
      setForm({
        status: 'error',
        message: error instanceof ApiError ? error.message : 'Could not save this ward subsidy rate.',
      })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print">
        <div>
          <h1 className="text-3xl mb-2">Ward subsidy rates</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            The CPW, FAW and CWM subsidy rates, keyed by scheme, ward class and
            service-date window. Effective-dated, PSD-only. MSO and CCS are flat-rate schemes with no ward axis and
            are not administered here.
          </p>
        </div>
        <div className="flex gap-2">
          {/*
            ★★★ WAVE 27 — one control group, owner-directed. These answer a single
            question — "what do I want to do with this list?" — and were arranged as if
            they answered three or four. See `ExportToolbar` for the whole argument.
          */}
          <ExportToolbar
            onDownload={(format) => {
              window.location.href = api.wardRuleExportUrl(format)
            }}
            email={<EmailReportButton path="/api/reference/ward-rules/export" />}
          />
        </div>
      </div>

      <AuthoringBoundary />

      {/*
        ⚠ The SERVER's sentence, rendered only while the server is still saying it.
        An `emptyRegisterNotice` that this file held would keep telling an officer
        no rate exists on the day after they authored one.
      */}
      {emptyRegisterNotice !== undefined && (
        <SgdsAlert show variant="info" title="The register is empty, and that is the correct state">
          <p className="text-sm">{plainProse(emptyRegisterNotice)}</p>
        </SgdsAlert>
      )}

      <div className="pt-controlbar pt-no-print">
        <SgdsSelect label="Scheme" value={scheme} onSgdsChange={(event) => setScheme(targetValue(event))}>
          <SgdsSelectOption value="">All ward schemes</SgdsSelectOption>
          {(wardRules.data?.schemes ?? []).map((value) => (
            <SgdsSelectOption key={value} value={value}>
              {value}
            </SgdsSelectOption>
          ))}
        </SgdsSelect>
      </div>

      <SortAnnouncement
        sort={sort}
        interacted={interacted}
        busy={wardRules.loading}
        columnLabels={COLUMN_LABELS}
        rowCount={rows.length}
      />

      {wardRules.error !== null ? (
        <ErrorCard title="Could not load the ward rate register" message={wardRules.error} onRetry={wardRules.reload} />
      ) : wardRules.initialLoading ? (
        <LoadingCard label="Loading ward subsidy rates" rows={4} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No ward subsidy rate is in force"
          message="Either nothing has been authored (which is the shipped state of this product) or your active identity is not PSD. This register is administered by PSD only."
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={wardRules.loading}>
            <table className="pt-table">
              <caption className="sr-only">Ward subsidy rates currently in force, one row per rule version</caption>
              <thead>
                <tr>
                  <SortableTh label="Rule" sortKey="id" sort={sort} onSort={onSort} />
                  <SortableTh label="Scheme" sortKey="scheme" sort={sort} onSort={onSort} />
                  <SortableTh label="Ward" sortKey="wardClass" sort={sort} onSort={onSort} />
                  <SortableTh label="Subsidy rate" sortKey="subsidyBasisPoints" sort={sort} onSort={onSort} align="right" />
                  <SortableTh label="Effective from" sortKey="effectiveFrom" sort={sort} onSort={onSort} />
                  <SortableTh label="Recorded" sortKey="recordedAt" sort={sort} onSort={onSort} />
                  {/* ⚠ Not sortable and not data: one control per row, and a
                      sortable header over buttons would claim to order something
                      with no order. The same exemption `WardClassesPage` states. */}
                  <th scope="col" className="pt-no-print" data-unsortable="one control per row, not data — a sortable header over buttons would claim to order something with no order">
                    Versions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.id}:${row.version}`}>
                    <td className="font-semibold pt-mono">
                      {row.id} <span className="text-xs text-[var(--sgds-color-subtle)]">v{row.version}</span>
                    </td>
                    <td><CodeBadge value={row.scheme} /></td>
                    <td className="pt-mono">{row.wardClass}</td>
                    <td className="pt-num pt-tabular">{ratePercent(row.subsidyBasisPoints)}</td>
                    <td className="text-sm">{formatIsoDate(row.effective.from)}</td>
                    <td className="text-sm" title={formatInstant(row.recordedAt)}>
                      {/*
                        ⚠ WHO is deliberately absent and WHETHER is not. P2C11 §3.13
                        keeps the officer's id off this surface; the boolean is what a
                        reader actually needs, because it decides whether a provenance
                        note was mandatory for the row.
                      */}
                      {row.authored ? 'Authored on this screen' : 'Migration or seed'}
                    </td>
                    <td className="pt-no-print">
                      <SgdsButton
                        variant="outline"
                        type="button"
                        onClick={() => {
                          setHistoryId(row.id)
                        }}
                      >
                        {historyId === row.id ? 'Showing' : 'Versions'}
                      </SgdsButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/*
            ⚠ THE PROVENANCE COLUMN IS A ROW OF ITS OWN, under the table rather than
            inside it. A note is a sentence of up to 1,000 characters and putting it
            in a cell would either truncate it — on the one surface where the whole
            sentence is the point — or make every row four lines tall.
          */}
          <div className="p-4 border-t border-[var(--sgds-border-color-default)]">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)]">
              Where each rate came from
            </p>
            <ul className="mt-2 flex flex-col gap-2">
              {rows.map((row) => (
                <li key={`${row.id}:${row.version}:note`} className="text-sm">
                  <span className="pt-mono">
                    {row.id} v{row.version}
                  </span>:{' '}
                  {row.note === null ? (
                    /*
                      ⚠ A row with no note is NOT reported as "no provenance recorded"
                      in a way that reads as an accusation. It genuinely means the row
                      came from a migration or the demonstration seed, which is the
                      only way an un-noted row can exist: the database refuses one from
                      an authoring officer.
                    */
                    <span className="text-[var(--sgds-body-color-subtle)]">
                      No note. This row was written by a migration or the demonstration seed, not authored here:
                      the database refuses an authored rate with no stated source.
                    </span>
                  ) : (
                    /* ⚠ VERBATIM. Every word is the authoring officer's. */
                    row.note
                  )}
                </li>
              ))}
            </ul>
          </div>
          <p className="px-4 pb-4 text-sm text-[var(--sgds-body-color-subtle)]">
            {rows.length} of {total} rate(s) currently in force.
          </p>
        </div>
      )}

      {historyId.length > 0 && (
        <div className="pt-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold mb-1">
                Every version of <span className="pt-mono">{historyId}</span>
              </h2>
              <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
                Oldest first. A bill records the rule id <em>and</em> the version it was priced under, so a
                superseding version never changes what an existing bill says it was charged at.
              </p>
            </div>
            <SgdsButton
              variant="outline"
              type="button"
              onClick={() => {
                setHistoryId('')
              }}
            >
              Close
            </SgdsButton>
          </div>
          {history.error !== null ? (
            <div className="mt-4">
              <ErrorCard title="Could not load this rule's versions" message={history.error} onRetry={history.reload} />
            </div>
          ) : history.initialLoading ? (
            <div className="mt-4">
              <LoadingCard label="Loading versions" rows={2} />
            </div>
          ) : (
            <ol className="mt-4 flex flex-col gap-3">
              {(history.data?.entries ?? []).map((entry: WardRule) => (
                <li key={entry.version} className="pt-well p-3">
                  <p className="font-semibold">
                    v{entry.version}, {ratePercent(entry.subsidyBasisPoints)} from{' '}
                    {formatIsoDate(entry.effective.from)}
                    {entry.effective.until === null ? ' (in force)' : ` until ${formatIsoDate(entry.effective.until)}`}
                  </p>
                  <p className="text-sm mt-1">
                    {entry.note ?? 'No note: written by a migration or the demonstration seed.'}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      <form className="pt-card pt-no-print" onSubmit={handleSubmit}>
        <h2 className="text-lg font-semibold mb-1">Author a rate</h2>
        <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch] mb-4">
          A new version supersedes whatever is in force for this scheme and ward from the service date you give. It
          cannot be back-dated over a window that has already priced bills: every rule is resolved on the
          <strong> date of medical service</strong>, so re-opening a past window would silently re-price settled work.
        </p>

        <div className="grid gap-4 md:grid-cols-2 max-w-3xl">
          <SgdsSelect label="Scheme" value={formScheme} onSgdsChange={(event) => setFormScheme(targetValue(event))}>
            {(wardRules.data?.schemes ?? ['CPW', 'FAW', 'CWM']).map((value) => (
              <SgdsSelectOption key={value} value={value}>
                {value}
              </SgdsSelectOption>
            ))}
          </SgdsSelect>

          <BoundedField
            label={BOUNDED_FIELDS.wardClass}
            value={wardClass}
            onValueChange={setWardClass}
            bounds={validation?.wardClass}
            problem={problems.of.wardClass}
            required
            example="e.g. B1"
          />

          {!retireInstead && (
            <BoundedField
              label={BOUNDED_FIELDS.subsidyBasisPoints}
              value={basisPoints}
              onValueChange={setBasisPoints}
              bounds={validation?.subsidyBasisPoints}
              problem={basisPointsProblem}
              required
              inputMode="numeric"
              example="Basis points, so 6500 is 65.00%."
            />
          )}

          <label className="flex flex-col gap-1">
            <span className="text-sm font-semibold">Effective from (date of medical service)</span>
            <DatePicker value={effectiveFrom} onChange={setEffectiveFrom} required />
          </label>
        </div>

        {!retireInstead && (
          <div className="mt-4 max-w-3xl">
            <BoundedField
              label={BOUNDED_FIELDS.note}
              value={note}
              onValueChange={setNote}
              bounds={validation?.note}
              problem={problems.of.note}
              required
              multiline
              rows={3}
              example="Name the source: a circular, a letter, or “provisional, pending PSD confirmation”. This sentence is printed beside every figure this rate produces."
            />
          </div>
        )}

        <label className="mt-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={retireInstead}
            onChange={(event) => {
              setRetireInstead(event.target.checked)
            }}
          />
          <span>
            Retire the rate in force for this scheme and ward instead of superseding it. From that date a bill for
            this ward is <strong>refused</strong>: it does not fall back to the previous rate and is not
            priced at zero.
          </span>
        </label>

        <div className="mt-4">
          <SgdsButton
            variant="primary"
            type="submit"
            disabled={!canSubmit || confirming !== null}
            loading={form.status === 'submitting'}
          >
            {retireInstead ? 'Retire this rate' : 'Author this rate'}
          </SgdsButton>
        </div>

        {confirming !== null && (
          <div className="mt-4 max-w-3xl">
            <ConfirmWrite
              summary={confirming}
              confirmLabel="Yes, apply it"
              variant="danger"
              onConfirm={() => void performWrite()}
              onCancel={() => setConfirming(null)}
            />
          </div>
        )}
        <FormResult state={form} />
      </form>
    </div>
  )
}
